import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from './api.service';

/** Store closed dates ("holiday closures"): set in /admin/closures, announced by the
 *  site-wide banner and in the order-placed popup. */

export type ClosureStatus = 'Draft' | 'Scheduled' | 'Published' | 'Stopped' | 'Expired';

export interface HolidayClosure {
  id: string;
  title: string;
  message: string;
  /** "YYYY-MM-DD" store days, both inclusive: the days the banner and the order note
   *  are up. Customers never see them - the message carries the dates to announce. */
  startDate: string;
  endDate: string;
  status: ClosureStatus | '';
  /** Set when the closure was ended early with /stop. */
  stoppedOn: string;
}

/** The backend's names, in the order of its 1-5 enum. Sent as the name (its documented
 *  format); a numeric code coming back is mapped by this order.
 *  Draft -> Scheduled -> Published (on the site) -> Stopped (ended early) / Expired. */
export const CLOSURE_STATUSES: ClosureStatus[] = ['Draft', 'Scheduled', 'Published', 'Stopped', 'Expired'];

function toStatus(value: any): ClosureStatus | '' {
  if (typeof value === 'number') return CLOSURE_STATUSES[value - 1] || '';
  const text = String(value ?? '').trim().toLowerCase();
  if (/^\d+$/.test(text)) return CLOSURE_STATUSES[Number(text) - 1] || '';
  return CLOSURE_STATUSES.find(s => s.toLowerCase() === text) || '';
}

/** "2026-12-24" out of "2026-12-24", "2026-12-24T00:00:00" or "2026-12-24T00:00:00Z".
 *  The dates are store days, not instants, so no timezone is applied. */
export function toDay(value: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value || '');
  return m ? m[1] : '';
}

/** "Thu, Dec 24" for a "YYYY-MM-DD" day. Formatted in UTC so the day never shifts
 *  with the viewer's timezone. */
export function formatDay(value: string, withYear = false): string {
  const day = toDay(value);
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** The API may name keys in either case, depending on its serializer settings. */
function pick(row: any, key: string): any {
  if (!row) return undefined;
  return row[key] ?? row[key.charAt(0).toUpperCase() + key.slice(1)];
}

export function normalizeClosure(row: any): HolidayClosure {
  return {
    id: pick(row, 'holidayClosureId') || pick(row, 'id') || '',
    title: pick(row, 'title') || '',
    message: pick(row, 'message') || '',
    startDate: toDay(pick(row, 'startDate')),
    endDate: toDay(pick(row, 'endDate')),
    // The API sends both: `status` as the 1-5 code, `statusInWords` as its name.
    status: toStatus(pick(row, 'statusInWords')) || toStatus(pick(row, 'status')),
    stoppedOn: pick(row, 'stoppedOn') || '',
  };
}

export function closureRows(res: any): HolidayClosure[] {
  const rows = Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.items) ? res.items
    : res && typeof res === 'object' && (res.holidayClosureId || res.HolidayClosureId || res.id) ? [res]
    : [];
  return rows.map(normalizeClosure);
}

/** Soonest first - the banner announces the next closure. */
function byStart(rows: HolidayClosure[]): HolidayClosure[] {
  return rows.sort((a, b) => a.startDate.localeCompare(b.startDate));
}

@Injectable({ providedIn: 'root' })
export class HolidayClosureService {
  private pending: Promise<HolidayClosure[]> | null = null;
  private readonly rows = signal<HolidayClosure[]>([]);

  /** The closures being announced now. Empty until the browser's live call answers. */
  readonly current = this.rows.asReadonly();

  private readonly isBrowser: boolean;

  constructor(private api: ApiService, @Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** Fetched once per page load, in the browser only and always live. The server never
   *  asks: server.ts caches whole pages for up to an hour, so a banner rendered there
   *  outlived its closure - visitors saw an ended closure flash up and vanish once the
   *  live call came back empty (reported 2026-10-05). A failure means "no notice",
   *  never a broken page. */
  active(): Promise<HolidayClosure[]> {
    if (!this.isBrowser) return Promise.resolve([]);
    return this.pending || this.fetch();
  }

  /** Re-asks the API. The admin page calls this after every change so the banner
   *  follows without a reload. */
  refresh(): Promise<HolidayClosure[]> {
    if (!this.isBrowser) return Promise.resolve([]);
    return this.fetch();
  }

  private fetch(): Promise<HolidayClosure[]> {
    const request = this.api.getActiveHolidayClosures()
      .then(res => {
        const rows = byStart(closureRows(res));
        this.rows.set(rows);
        return rows;
      })
      .catch(() => {
        if (this.pending === request) this.pending = null;
        return this.rows();
      });
    this.pending = request;
    return request;
  }
}
